import { useState, useEffect, useCallback } from 'react';

// Check if browser supports notifications
const isNotificationSupported = () => 'Notification' in window;

// Check if permission is granted
const isPermissionGranted = () => Notification.permission === 'granted';

export function usePushNotifications() {
    const [permission, setPermission] = useState(
        isNotificationSupported() ? Notification.permission : 'denied'
    );
    const [isSupported, setIsSupported] = useState(false);

    useEffect(() => {
        setIsSupported(isNotificationSupported());
        if (isNotificationSupported()) {
            setPermission(Notification.permission);
        }
    }, []);

    const requestPermission = useCallback(async () => {
        if (!isNotificationSupported()) {
            console.log('Notifications not supported');
            return false;
        }

        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            return result === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }, []);

    const sendNotification = useCallback((title, options = {}) => {
        if (!isNotificationSupported() || !isPermissionGranted()) {
            return null;
        }

        try {
            const notification = new Notification(title, {
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: options.tag || 'bet4peer',
                requireInteraction: false,
                silent: false,
                ...options,
            });

            // Auto-close after 5 seconds
            setTimeout(() => {
                notification.close();
            }, 5000);

            notification.onclick = () => {
                window.focus();
                if (options.onClick) {
                    options.onClick();
                }
                notification.close();
            };

            return notification;
        } catch (error) {
            console.error('Error sending notification:', error);
            return null;
        }
    }, []);

    const notifyBetAccepted = useCallback((betDescription) => {
        return sendNotification('Bet Accepted', {
            body: `Someone accepted your bet: ${betDescription.slice(0, 50)}...`,
            tag: 'bet-accepted',
        });
    }, [sendNotification]);

    const notifyBetWon = useCallback((betDescription, payout) => {
        return sendNotification('You Won', {
            body: `You won ${payout.toFixed(4)} ETH on: ${betDescription.slice(0, 40)}...`,
            tag: 'bet-won',
        });
    }, [sendNotification]);

    const notifyBetLost = useCallback((betDescription) => {
        return sendNotification('Bet Resolved', {
            body: `You lost the bet: ${betDescription.slice(0, 50)}...`,
            tag: 'bet-lost',
        });
    }, [sendNotification]);

    const notifyOracleAssertion = useCallback((betDescription, assertedWinner) => {
        return sendNotification('Oracle Resolution Started ⚖️', {
            body: `Assertion: ${assertedWinner} wins. 2h challenge window open.`,
            tag: 'oracle-assertion',
        });
    }, [sendNotification]);

    const notifyOracleDispute = useCallback((betDescription) => {
        return sendNotification('Assertion Disputed', {
            body: `The assertion for your bet was disputed. UMA voters will decide.`,
            tag: 'oracle-dispute',
        });
    }, [sendNotification]);

    return {
        isSupported,
        permission,
        isGranted: permission === 'granted',
        requestPermission,
        sendNotification,
        notifyBetAccepted,
        notifyBetWon,
        notifyBetLost,
        notifyOracleAssertion,
        notifyOracleDispute,
    };
}
